import jsPDF from 'jspdf';

import type { DashboardPaymentSummaryItem, DashboardViewType } from '../types/vida/table/dashboardPaymentSummaryTableType';

/**
 * Sanitizes a string for use in filename
 */
const sanitizeFileName = (str: string): string => {
  return str.replace(/[^a-zA-Z0-9]/g, '_');
};

/**
 * Converts hex color to RGB
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
    : null;
};

/**
 * Helper function to add text with word wrap to PDF document
 */
const addTextToPdf = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  fontSize: number = 12,
  isBold: boolean = false
): number => {
  doc.setFontSize(fontSize);
  doc.setFont('helvetica', isBold ? 'bold' : 'normal');
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, x, y);
  return y + (lines.length * fontSize * 0.4);
};

/**
 * Creates a header section in PDF with title
 */
const addPdfHeader = (
  doc: jsPDF,
  title: string,
  yPosition: number,
  pageWidth: number,
  margin: number
): number => {
  doc.setFillColor(220, 38, 38); // #dc2626
  doc.rect(margin, yPosition, pageWidth - (margin * 2), 30, 'F');
  doc.setTextColor(255, 255, 255);
  const newY = addTextToPdf(doc, title, margin + 10, yPosition + 20, pageWidth - (margin * 2), 24, true);
  doc.setTextColor(0, 0, 0);
  return newY + 10;
};

/**
 * Adds a label-value pair to PDF in two-column layout
 */
const addLabelValuePair = (
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  columnWidth: number
): number => {
  doc.setFontSize(10);
  doc.setTextColor(102, 102, 102);
  let newY = addTextToPdf(doc, label, x, y, columnWidth, 10, true);
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  newY = addTextToPdf(doc, value, x, newY + 5, columnWidth, 12);
  return newY + 10;
};

/**
 * Gets status color for dashboard payment summary badge styling
 */
const getDashboardStatusColor = (status?: string): string => {
  if (!status) return '#6b7280';

  const statusColors: Record<string, string> = {
    'Pago': '#10b981',
    'Em dia': '#10b981',
    'Pendente': '#f59e0b',
    'Atenção': '#f59e0b',
    'Vencido': '#ef4444',
    'Atrasado': '#ef4444',
    'Cancelado': '#6b7280',
  };
  return statusColors[status] || '#6b7280';
};

/**
 * Generates a PDF document for dashboard payment summary item and downloads it automatically
 * @param item - Dashboard payment summary item data to export
 * @param viewType - Type of view (corretora, estipulante, apolice)
 * @param formatCurrency - Function to format currency values
 * @param formatPercent - Function to format percentage values
 */
export const generateDashboardPaymentSummaryPDF = (
  item: DashboardPaymentSummaryItem,
  viewType: DashboardViewType,
  formatCurrency: (value: number) => string,
  formatPercent: (value: number) => string
): void => {
  // Get column title based on view type
  const getColumnTitle = () => {
    switch (viewType) {
      case 'corretora':
        return 'Corretora';
      case 'estipulante':
        return 'Estipulante';
      case 'apolice':
        return 'Apólice';
      default:
        return 'Nome';
    }
  };

  const columnTitle = getColumnTitle();
  const sanitizedName = sanitizeFileName(item.name);
  const fileName = `Resumo_${sanitizedName}.pdf`;

  // Create new PDF document
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let yPosition = margin;

  // Header
  yPosition = addPdfHeader(doc, 'RESUMO DE PAGAMENTOS', yPosition, pageWidth, margin);

  // Item information
  const infoStartY = yPosition;
  const leftColumnX = margin;
  const rightColumnX = pageWidth / 2 + 10;
  const columnWidth = (pageWidth - margin * 2) / 2 - 10;

  // Left column
  yPosition = infoStartY;
  yPosition = addLabelValuePair(doc, columnTitle.toUpperCase(), item.name, leftColumnX, yPosition, columnWidth);
  yPosition = addLabelValuePair(doc, '% PENDENTE', formatPercent(item.percentPending), leftColumnX, yPosition, columnWidth);
  yPosition = addLabelValuePair(doc, '% EMITIDO', formatPercent(item.percentIssued), leftColumnX, yPosition, columnWidth);

  // Right column
  let rightY = infoStartY;
  rightY = addLabelValuePair(doc, 'VALOR PENDENTE', formatCurrency(item.valuePending), rightColumnX, rightY, columnWidth);
  rightY = addLabelValuePair(doc, 'VALOR EMITIDO', formatCurrency(item.valueIssued), rightColumnX, rightY, columnWidth);

  if (item.contractNumber) {
    rightY = addLabelValuePair(doc, 'NÚMERO DO CONTRATO', item.contractNumber, rightColumnX, rightY, columnWidth);
  }

  // Status section
  const statusY = Math.max(yPosition, rightY) + 10;
  doc.setFontSize(10);
  doc.setTextColor(102, 102, 102);
  const statusLabelY = addTextToPdf(doc, 'STATUS', leftColumnX, statusY, columnWidth, 10, true);
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);

  // Status badge background
  const statusColor = getDashboardStatusColor(item.status);
  const rgb = hexToRgb(statusColor);
  if (rgb && item.status) {
    doc.setFillColor(rgb.r, rgb.g, rgb.b);
    doc.roundedRect(leftColumnX, statusLabelY + 2, 50, 8, 2, 2, 'F');
  }
  doc.setTextColor(255, 255, 255);
  addTextToPdf(doc, (item.status || '-').toUpperCase(), leftColumnX + 5, statusLabelY + 8, 45, 10, true);

  // Save PDF
  doc.save(fileName);
};

